import { useMemo } from 'react'
import styled, { useTheme } from 'styled-components'
import { strings } from '@constants'
import { capitalizeWord } from '@utils'
import { DeleteOutline, EditOutlined } from '@material-ui/icons'
// Imported directly (not from the '@material-ui/icons' barrel) because this file only
// ever references it as a default parameter value, never as a JSX tag — a usage shape
// babel-plugin-import's per-icon rewrite doesn't recognize, which silently drops the
// barrel import for it while still removing the original import declaration.
import PaymentOutlined from '@material-ui/icons/PaymentOutlined'
import paymentTermsEmptyImg from '@assets/icons/payment-terms-empty.jpg'

import Table, { TableActionsWrapper, TableActionButton } from '../table'
import CardHeaderV2 from '../cardHeader'
import {
	PaymentTermsTableWrapper,
	PaymentTermsEmptyState,
	PaymentTermsEmptyImage,
	PaymentTermsEmptyTitle,
	PaymentTermsEmptyText,
} from './paymentTermsCardStyles'

const formatCondition = (condition) =>
	condition
		? condition
				.split('_')
				.map((part) => capitalizeWord(part))
				.join(' ')
		: '-'

const RequiredMark = styled.span`
	color: ${({ theme }) => theme.colors.status.error.text};
	margin-left: 2px;
`

const PaymentTermsCard = ({ paymentTermsList, onEdit, onDelete, icon: Icon = PaymentOutlined, emptyText, actionVariant, disabled = false, required = false, showAction = true }) => {
	const theme = useTheme()
	const rows = useMemo(() => paymentTermsList ?? [], [paymentTermsList])

	const columns = useMemo(() => {
		const cols = [
			{
				label: strings('payment', 'method'),
				key: (row) => `${capitalizeWord(row?.payment_term ?? '-')}${row?.type != null ? `-${row.type}` : ''}`,
				sortable: false,
			},
			{ label: strings('tenure_in_days'), key: 'tenure_in_days', default: '-', sortable: false, align: 'center', mobile: false },
			{
				label: strings(['percentage', '(', '%', ')'], '', ''),
				key: 'percentage',
				default: '-',
				sortable: false,
				align: 'center',
			},
			{ label: strings('condition'), key: (row) => formatCondition(row?.condition), sortable: false, mobile: false },
		]
		if (onDelete) {
			cols.push({
				label: strings('actions'),
				align: 'right',
				sortable: false,
				 mobile: true,
				renderer: (_v, row) => {
					const index = rows.indexOf(row)
					return (
						<TableActionsWrapper>
							<TableActionButton disabled={disabled} color={theme.colors.status.error.text} onClick={() => onDelete(index)}>
								<DeleteOutline />
							</TableActionButton>
							<TableActionButton disabled={disabled} color={theme.colors.brand.primary} onClick={onEdit}>
								<EditOutlined />
							</TableActionButton>
						</TableActionsWrapper>
					)
				},
			})
		}
		return cols
	}, [rows, onDelete, onEdit, theme, disabled])

	return (
		<>
			<CardHeaderV2
				title={
					<span>
						{strings('payment_terms')}
						{required && <RequiredMark>*</RequiredMark>}
					</span>
				}
				icon={Icon}
				showAction={showAction}
				actionLabel={paymentTermsList?.length ? strings('edit') : strings('add')}
				onAction={onEdit}
				actionVariant={actionVariant}
				disabled={disabled}
			/>
			{!emptyText && rows.length === 0 ? (
				<PaymentTermsEmptyState>
					<PaymentTermsEmptyImage src={paymentTermsEmptyImg} alt='' />
					<PaymentTermsEmptyTitle>{strings('no_data_found_title')}</PaymentTermsEmptyTitle>
					<PaymentTermsEmptyText>{strings('msg_select_method_to_view_payment_terms')}</PaymentTermsEmptyText>
				</PaymentTermsEmptyState>
			) : (
				<PaymentTermsTableWrapper>
					<Table columns={columns} data={rows} hideFooter emptyState={emptyText ? { text: emptyText } : undefined} />
				</PaymentTermsTableWrapper>
			)}
		</>
	)
}

export default PaymentTermsCard
